import React from 'react';
import { ActivityIndicator, Image, Pressable, StatusBar } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import UsKidsLogo from '@/assets/images/logo-uskids-portrait.png';
import { Scroll, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useGuardianInvitation } from '@/features/guardian/hooks/use-guardian-invitation';
import { GuardianInvitationSubmissionAction } from '@/features/guardian/types/invitation';
import { createStyles, useScaling } from '@/hooks';
import type { RootStackScreenProps } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';

const INVITE_STRINGS = STRINGS.guardian.coGuardianInvite;

function AccessList({ playerName }: { playerName: string }) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack gap={scale(8)}>
      {INVITE_STRINGS.accessItems(playerName).map(item => (
        <Stack key={item} direction="row" align="flex-start" gap={scale(8)}>
          <SvgIcon name="IcCheckMd" size={scale(16)} color={guardianColors['10']} />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.accessText}>
            {item}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
}

function AccessDetails({ playerName }: { playerName: string }) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Stack style={styles.accessDetails} gap={scale(9)}>
      <Stack direction="row" align="center" gap={scale(4)}>
        <SvgIcon name="IcCheckListMd" size={scale(16)} color={guardianColors['6']} />
        <Text variant="Font Guardian App/Subtitle/Subtitle 11 Semibold" style={styles.sectionLabel}>
          {INVITE_STRINGS.accessTitle}
        </Text>
      </Stack>
      <AccessList playerName={playerName} />
    </Stack>
  );
}

function InvitationMessage({ inviterName, message }: { inviterName: string; message: string }) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <LinearGradient
      colors={['#D7E0ED', colors['0']]}
      locations={[0.3023, 0.9214]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      useAngle
      angle={152.7}
      style={styles.messageCardWrapper}>
      <Stack style={styles.messageCard}>
        <Stack style={styles.avatar} align="center" justify="center">
          <Text
            style={{ color: guardianColors['Main/Main'] }}
            fontFamily="Montserrat"
            fontWeight="700"
            fontSize={scale(20)}
            lineHeight={scale(28)}>
            {inviterName.trim().charAt(0).toUpperCase()}
          </Text>
        </Stack>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Italic"
          style={styles.messageText}
          fontStyle="italic">
          &quot;
          {message}
          &quot; — {inviterName}
        </Text>
      </Stack>
    </LinearGradient>
  );
}

type ActionButtonsProps = {
  onAccept: () => void;
  onDecline: () => void;
  submittingAction?: GuardianInvitationSubmissionAction;
};

function ActionButtons({ onAccept, onDecline, submittingAction }: ActionButtonsProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const disabled = submittingAction !== undefined;
  const isAccepting = submittingAction === GuardianInvitationSubmissionAction.Accept;
  const isDeclining = submittingAction === GuardianInvitationSubmissionAction.Decline;

  return (
    <Stack style={styles.actions} gap={scale(12)}>
      <Pressable
        testID="guardian-invitation-accept-button"
        accessibilityRole="button"
        accessibilityLabel={INVITE_STRINGS.acceptAccessibilityLabel}
        accessibilityState={{ busy: isAccepting, disabled }}
        disabled={disabled}
        onPress={onAccept}
        pointerEvents="box-only"
        style={({ pressed }) => [
          styles.primaryButton,
          pressed && styles.buttonPressed,
          disabled && styles.buttonDisabled,
        ]}>
        {isAccepting ? (
          <ActivityIndicator size="small" color={colors['0']} />
        ) : (
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            style={styles.primaryLabel}>
            {INVITE_STRINGS.accept}
          </Text>
        )}
      </Pressable>
      <Pressable
        testID="guardian-invitation-decline-button"
        accessibilityRole="button"
        accessibilityLabel={INVITE_STRINGS.declineAccessibilityLabel}
        accessibilityState={{ busy: isDeclining, disabled }}
        disabled={disabled}
        onPress={onDecline}
        pointerEvents="box-only"
        style={({ pressed }) => [
          styles.secondaryButton,
          pressed && styles.buttonPressed,
          disabled && styles.buttonDisabled,
        ]}>
        {isDeclining ? (
          <ActivityIndicator size="small" color={guardianColors.Error} />
        ) : (
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            style={styles.secondaryLabel}>
            {INVITE_STRINGS.decline}
          </Text>
        )}
      </Pressable>
    </Stack>
  );
}

export function GuardianCoGuardianInviteScreen({
  route,
}: RootStackScreenProps<'GuardianInvitation'>) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();
  const { token } = route.params;
  const {
    invitation,
    invitedEmail,
    expiryDate,
    isLoading,
    submittingAction,
    handleAccept,
    handleDecline,
  } = useGuardianInvitation(token);

  if (isLoading || !invitation) {
    return (
      <LinearGradient
        colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
        locations={[0.08, 0.47, 0.9]}
        useAngle
        angle={210.65}
        style={styles.loadingScreen}>
        <ActivityIndicator size="large" color={guardianColors['Main/Main']} />
      </LinearGradient>
    );
  }

  return (
    <LinearGradient
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.9]}
      useAngle
      angle={210.65}
      style={styles.screen}>
      <StatusBar barStyle="dark-content" backgroundColor="transparent" translucent />
      <Scroll
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: insets.top + scale({ phone: 28, tablet: 48 }),
            paddingBottom: insets.bottom + scale({ phone: 22, tablet: 61 }),
          },
        ]}>
        <Stack style={styles.card} align="center" gap={scale(20)}>
          <Image source={UsKidsLogo} resizeMode="contain" style={styles.logo} />

          <Stack style={styles.heading} align="center">
            <Text
              variant={
                isTablet
                  ? 'Font Guardian App/Heading/Heading H5 24px'
                  : 'Font Guardian App/Body/Body 16 Bold'
              }
              fontSize={isTablet ? scale(32) : undefined}
              lineHeight={isTablet ? scale(44.8) : undefined}
              style={styles.centerText}>
              {INVITE_STRINGS.title}
            </Text>
            <Text
              variant={
                isTablet
                  ? 'Font Guardian App/Body/Body 16 Bold'
                  : 'Font Guardian App/Subtitle/Subtitle 14 Bold'
              }
              style={styles.email}>
              {invitedEmail}
            </Text>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.centerText}>
              {INVITE_STRINGS.description(invitation.invitedByName, invitation.playerName)}
            </Text>
          </Stack>

          <Stack style={styles.details} direction={isTablet ? 'row' : 'column'} gap={scale(16)}>
            <AccessDetails playerName={invitation.playerName} />
            {Boolean(invitation.message?.trim()) && (
              <InvitationMessage
                inviterName={invitation.invitedByName}
                message={invitation.message!.trim()}
              />
            )}
          </Stack>

          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            fontStyle="italic"
            style={styles.expiry}>
            {INVITE_STRINGS.expiry(expiryDate)}
          </Text>

          <ActionButtons
            onAccept={handleAccept}
            onDecline={handleDecline}
            submittingAction={submittingAction}
          />

          <Stack style={styles.footer} align="center" gap={scale(8)}>
            <Stack style={styles.divider} />
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.copyright}>
              {INVITE_STRINGS.copyright}
            </Text>
          </Stack>
        </Stack>
      </Scroll>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  screen: { flex: 1 },
  loadingScreen: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scrollContent: {
    minHeight: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: scale(16),
  },
  card: {
    width: scale({ phone: 343, tablet: 664 }),
    maxWidth: '100%',
    paddingHorizontal: scale(24),
    paddingVertical: scale(20),
    borderRadius: scale(20),
    backgroundColor: colors['0'],
  },
  logo: {
    width: scale(220),
    height: scale(120),
  },
  heading: {
    width: '100%',
    gap: isTablet ? scale(8) : 0,
  },
  centerText: {
    width: '100%',
    textAlign: 'center',
    color: guardianColors['10'],
  },
  email: {
    width: '100%',
    textAlign: 'center',
    color: guardianColors['Main/Main'],
  },
  details: { width: '100%' },
  accessDetails: {
    width: isTablet ? scale(300) : '100%',
    flexShrink: 0,
  },
  sectionLabel: { color: guardianColors['6'] },
  accessText: { flex: 1, color: guardianColors['10'] },
  messageCard: {
    flex: 1,
    minHeight: scale({ phone: 77, tablet: 129 }),
    paddingHorizontal: scale(12),
    paddingVertical: scale(10),
    borderWidth: scale(1),
    borderColor: guardianColors['Main/Main'],
    borderRadius: scale(12),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(10),
  },
  messageCardWrapper: {
    width: isTablet ? scale(300) : '100%',
    minHeight: scale({ phone: 77, tablet: 129 }),
    alignSelf: 'stretch',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: scale(12),
  },
  avatar: {
    width: scale(48),
    height: scale(48),
    borderRadius: scale(24),
    borderWidth: scale(3),
    borderColor: colors['0'],
    backgroundColor: guardianColors['8'],
  },
  messageText: {
    flex: 1,
    color: guardianColors['10'],
  },
  expiry: {
    width: '100%',
    textAlign: 'center',
    color: guardianColors['6'],
  },
  actions: {
    width: scale({ phone: 295, tablet: 300 }),
    height: scale(108),
  },
  primaryButton: {
    width: '100%',
    height: scale(48),
    flexShrink: 0,
    zIndex: 0,
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['Main/Main'],
    shadowColor: '#292757',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: scale(1) },
    shadowRadius: scale(1),
    elevation: 1,
  },
  secondaryButton: {
    width: '100%',
    height: scale(48),
    flexShrink: 0,
    zIndex: 1,
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['0'],
  },
  buttonPressed: { opacity: 0.7 },
  buttonDisabled: { opacity: 0.5 },
  primaryLabel: { color: colors['0'] },
  secondaryLabel: { color: guardianColors.Error },
  footer: { width: '100%' },
  divider: {
    width: '100%',
    height: scale(1),
    backgroundColor: guardianColors['3'],
  },
  copyright: { color: colors['Labels/Primary'], textAlign: 'center' },
}));
